import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Shield, LayoutDashboard, AlertTriangle, FileText, Power, 
  Clock, Network, LogOut, Menu, X, RefreshCw, ChevronDown,
  Users, AlertOctagon, BarChart3, Eye, Lock, FileSearch, Server, Play,
  ClipboardCheck, Inbox
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardStats, triggerDiscoveryScan } from '../services/api';
import { toast } from 'sonner';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await triggerDiscoveryScan();
      toast.success('Discovery scan initiated');
      setTimeout(fetchStats, 2000);
    } catch (error) {
      toast.error('Failed to start scan');
    }
    setScanning(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', icon: BarChart3, label: 'Executive', exact: true },
    { path: '/dashboard/first-scan', icon: FileSearch, label: 'First Scan Report' },
    { path: '/dashboard/inventory', icon: Users, label: 'Inventory' },
    { path: '/dashboard/connectors', icon: Server, label: 'Connectors' },
    { path: '/dashboard/graph', icon: Network, label: 'Permission Graph' },
    { path: '/dashboard/deployment-gate', icon: Lock, label: 'Deployment Gate' },
    { path: '/dashboard/policy-simulator', icon: Play, label: 'Policy Simulator' },
    { path: '/dashboard/violations', icon: AlertTriangle, label: 'Violations', badge: stats?.open_violations },
    { path: '/dashboard/incidents', icon: AlertOctagon, label: 'Incidents' },
    { path: '/dashboard/policies', icon: FileText, label: 'Policies' },
    { path: '/dashboard/killswitch', icon: Power, label: 'Kill Switch' },
    { path: '/dashboard/audit', icon: Clock, label: 'Audit Log' },
    { path: '/dashboard/compliance', icon: ClipboardCheck, label: 'Compliance' },
    { path: '/dashboard/leads', icon: Inbox, label: 'Pilot Requests' },
    { path: '/dashboard/security', icon: Shield, label: 'Security' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#121212] border-r border-[#27272A]/40 transform transition-transform duration-200 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#27272A]/40">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-[#0066FF]" />
              <span className="text-lg font-bold text-[#F9FAFB] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Agent Control Plane
              </span>
            </Link>
          </div>

          {/* Stats Summary */}
          {stats && (
            <div className="px-4 py-4 border-b border-[#27272A]/40">
              <div className="grid grid-cols-2 gap-2">
                <div className="enterprise-card p-3 text-center">
                  <div className="text-xl font-mono font-bold text-[#0066FF]">{stats.total_agents}</div>
                  <div className="text-xs text-[#6B7280] uppercase">Agents</div>
                </div>
                <div className="enterprise-card p-3 text-center">
                  <div className="text-xl font-mono font-bold text-[#FF3B30]">{stats.open_violations}</div>
                  <div className="text-xs text-[#6B7280] uppercase">Violations</div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-150 ${
                  isActive(item.path, item.exact)
                    ? 'bg-[#0066FF]/10 text-[#0066FF] border-l-2 border-[#0066FF]'
                    : 'text-[#9CA3AF] hover:text-[#F9FAFB] hover:bg-[#1A1A1A]'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto bg-[#FF3B30] text-white text-xs px-2 py-0.5 rounded-none font-mono">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* Scan Button */}
          <div className="p-4 border-t border-[#27272A]/40">
            <Button
              onClick={handleScan}
              disabled={scanning}
              className="w-full secondary-btn flex items-center justify-center gap-2"
              data-testid="trigger-scan-btn"
            >
              <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scanning...' : 'Run Discovery Scan'}
            </Button>
          </div>

          {/* User Section */}
          <div className="p-4 border-t border-[#27272A]/40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#0066FF]/20 flex items-center justify-center">
                  <span className="text-[#0066FF] font-bold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#F9FAFB]">{user?.name || 'User'}</div>
                  <div className="text-xs text-[#6B7280]">{user?.role || 'user'}</div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="text-[#6B7280] hover:text-[#FF3B30] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#121212] border border-[#27272A] text-[#F9FAFB]"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet context={{ stats, refreshStats: fetchStats }} />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
